import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Fade, Slide } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Lottie from 'react-lottie-player/dist/LottiePlayerLight';
import { SessionManager } from '../utils/SessionManager';

// Import des images de fond
import backgroundOneImage from '../assets/images/backgrounds/backgroundOne.png';
import backgroundTwoImage from '../assets/images/backgrounds/backgroundTwo.png';
import backgroundThreeImage from '../assets/images/backgrounds/backgroundThree.png';

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');
  const [lottieAnimations, setLottieAnimations] = useState([null, null, null]);
  const [backgroundsLoaded, setBackgroundsLoaded] = useState([false, false, false]);
  const navigate = useNavigate();

  // Pré-charger les images de fond
  useEffect(() => {
    const backgrounds = [
      backgroundOneImage,
      backgroundTwoImage,
      backgroundThreeImage
    ];

    backgrounds.forEach((bg, index) => {
      const img = new Image();
      img.src = bg;
      img.onload = () => {
        setBackgroundsLoaded(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      };
    });
  }, []);

  // Charger les animations Lottie
  useEffect(() => {
    const loadAnimations = async () => {
      try {
        const anim1 = await fetch('/json_file/baoubab.json').then(r => r.json());
        const anim2 = await fetch('/json_file/Palette.json').then(r => r.json());
        const anim3 = await fetch('/json_file/Fille sur livres.json').then(r => r.json());
        setLottieAnimations([anim1, anim2, anim3]);
      } catch (error) {
        console.error('Erreur lors du chargement des animations Lottie:', error);
      }
    };
    loadAnimations();
  }, []);

  const slides = [
    {
      title: "Un monde d'éveil et de découvertes",
      description: "Un compagnon éducatif qui accompagne vos enfants pas à pas, avec vous",
      lottieIndex: 0,
      background: backgroundOneImage,
      backgroundColor:'rgba(65, 160, 201, 0.75)',
      buttonText: "Suivant"
    },
    {
      title: "Apprenez en vous amusant",
      description: "Des activités ludiques adaptées à l'âge de votre enfant pour un apprentissage efficace",
      lottieIndex: 1,
      background: backgroundTwoImage,
      backgroundColor:'rgba(71, 216, 88, 0.55)',
      buttonText: "Suivant"
    },
    {
      title: "Grandissez ensemble",
      description: "Suivez les progrès et célébrez chaque étape du développement de votre enfant",
      lottieIndex: 2,
      background: backgroundThreeImage,
      backgroundColor:'rgba(107, 71, 216, 0.75)',
      buttonText: "Commencer"
    }
  ];

  const handleNext = () => {
    if (currentStep < slides.length - 1) {
      setSlideDirection('left');
      setCurrentStep(currentStep + 1);
    } else {
      SessionManager.markAsVisited();
      navigate('/auth');
    }
  };

  const handleDotClick = (index) => {
    setSlideDirection(index > currentStep ? 'left' : 'right');
    setCurrentStep(index);
  };

  const handleSkip = () => {
    setCurrentStep(slides.length - 1);
  };

  return (
    <Box
      className="mobile-app-container"
      sx={{
        height: '100vh',
        width: '100%',
        backgroundImage: `url(${slides[currentStep].background})`,
        backgroundSize: 'cover',
        backgroundColor:`${slides[currentStep].backgroundColor}`,
        backgroundPosition: 'center',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background-image 0.6s ease-in-out',
        opacity: backgroundsLoaded[currentStep] ? 1 : 0.7,
        transitionProperty: 'opacity, background-image',
      }}
    >
      {/* Overlay pour améliorer la lisibilité */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.08) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal */}
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
          paddingBottom: '120px', // Espace pour les boutons
        }}
      >
        {/* En-tête avec logo */}
        <Box
          sx={{
            pt: 4,
            px: 3,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Fade in={true} timeout={800}>
            <Typography
              sx={{
                fontFamily: 'Achiko, sans-serif',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: 'white',
                letterSpacing: '2px',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
              }}
            >
               baoubab
            </Typography>
          </Fade>
        </Box>

        {/* Zone de contenu avec animation */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 3,
            py: 4,
            maxHeight: 'calc(100vh - 200px)', // Limiter la hauteur pour éviter le débordement
            overflow: 'hidden',
          }}
        >
          <Slide
            key={currentStep}
            direction={slideDirection}
            in={true}
            timeout={500}
            mountOnEnter
            unmountOnExit
          >
            <Box>
              {/* Texte */}
              <Box
                sx={{
                  textAlign: 'center',
                  mb: 6,
                  px: 2,
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontFamily: 'Achiko, sans-serif',
                    fontWeight: 'bold',
                    color: 'white',
                    mb: 2,
                    textShadow: '0 2px 15px rgba(0,0,0,0.3)',
                    fontSize: { xs: '1.75rem', sm: '2.125rem' },
                    lineHeight: 1.3,
                  }}
                >
                  {slides[currentStep].title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    maxWidth: '400px',
                    mx: 'auto',
                    textShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}
                >
                  {slides[currentStep].description}
                </Typography>
              </Box>

              {/* Animation Lottie avec animation flottante */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: { xs: '200px', sm: '250px' }, // Réduire sur mobile
                  maxHeight: { xs: '250px', sm: '300px' }, // Limiter la hauteur max
                  px: 4,
                }}
              >
                {lottieAnimations[slides[currentStep].lottieIndex] ? (
                  <Box
                    sx={{
                      width: { xs: '200px', sm: '250px' }, // Réduire sur mobile
                      height: { xs: '200px', sm: '250px' }, // Réduire sur mobile
                      filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
                      animation: 'float 3s ease-in-out infinite',
                      '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-10px)' },
                      },
                    }}
                  >
                    <Lottie
                      animationData={lottieAnimations[slides[currentStep].lottieIndex]}
                      loop
                      play
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: { xs: '200px', sm: '250px' }, // Réduire sur mobile
                      height: { xs: '200px', sm: '250px' }, // Réduire sur mobile
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    Chargement...
                  </Box>
                )}
              </Box>
            </Box>
          </Slide>
        </Box>

        {/* Footer avec indicateurs et bouton */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            pb: { xs: 4, sm: 5 },
            px: 3,
            zIndex: 10,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Indicateurs de progression (dots) */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1.5,
              mb: 4,
            }}
          >
            {slides.map((_, index) => (
              <Box
                key={index}
                onClick={() => handleDotClick(index)}
                sx={{
                  width: currentStep === index ? 28 : 10,
                  height: 10,
                  borderRadius: '5px',
                  backgroundColor: currentStep === index ? 'white' : 'rgba(255, 255, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: currentStep === index ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                  '&:hover': {
                    backgroundColor: currentStep === index ? 'white' : 'rgba(255, 255, 255, 0.6)',
                    transform: 'scale(1.1)',
                  },
                }}
              />
            ))}
          </Box>

          {/* Bouton d'action */}
          <Box sx={{ width: '100%', maxWidth: '400px' }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleNext}
              sx={{
                backgroundColor: 'white',
                color: '#333',
                borderRadius: '30px',
                padding: '14px 32px',
                fontWeight: '600',
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              {slides[currentStep].buttonText}
            </Button>
          </Box>

          {/* Lien "Passer" */}
          {currentStep < slides.length - 1 && (
            <Box sx={{ width: '100%', maxWidth: '400px' }}>
              <Button
                onClick={handleSkip}
                sx={{
                  width: '100%',
                  mt: 2,
                  color: 'rgba(255, 255, 255, 0.8)',
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                  },
                }}
              >
                Passer
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Signature en bas - cachée derrière le footer fixe */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 140, sm: 150 }, // Au-dessus du footer fixe
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: 0.4,
          zIndex: 5,
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: '300',
          }}
        >
          Par BOKONZI • Version 1.0.0
        </Typography>
      </Box>
    </Box>
  );
};

export default Onboarding;